import * as React from "react";
import {
    Badge,
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    MoreButton,
} from "@jhool-io/fe-components";
import { useSearchParams } from "react-router-dom";

import { useIsMutating } from "@tanstack/react-query";
import styles from "./CaseloadView.module.scss";
import { ProviderCaseloadApp } from "../../../types/schedule.types";
import { getColorStylesForCalendarId } from "../../../../../utils/constants";
import useShowInsuranceOverSelfPay from "../../../../practice-settings/hooks/useShowInsuranceOverSelfPay";

import {
    convertTimeTo12HoursFormat,
    displayNameInRightFormat,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
} from "../../../../../utils/helpers";
import { useDebounce } from "../../../../../hooks/helpers";
import { sortCaseloads } from "../../../helpers/schedule.helpers";
import moment from "../../../../../utils/moment";
import ListState from "../../../../../components/ListState/ListState";
import { useFetchProvidersCaseload } from "../../../../providers/hooks/providers.queries";
import { useAppSelector } from "../../../../../hooks/useRedux";
import CancelSessionForm from "./CancelSessionForm/CancelSessionForm";
import Loader from "../../../../../shared-ui/Loader/Loader";

// single event view

type Modals = "terminate" | "cancel";

function SingleSchedule({ schedule }: { schedule: ProviderCaseloadApp }) {
    const { recurrence_rule: recurrenceRule } = schedule;
    const colorStyles = getColorStylesForCalendarId(recurrenceRule as string);
    const style = {
        background: colorStyles.backgroundColor,
        borderLeft: colorStyles.color,
    };

    const [modalInView, setModalInView] = React.useState<Modals | null>(null);

    const handleModalInView = (modal: Modals | null) => {
        setModalInView(modal);
    };

    const [sessionHistoryId, setSessionHistoryId] = React.useState<
        string | null
    >(null);
    const [externalSessionId, setExternalSessionId] = React.useState<
        string | null
    >(null);
    const [intakeClientId, setIntakeClientId] = React.useState<string | null>(
        null
    );
    const isCancelSessionMutating = useIsMutating(["cancel-session"]);

    const showInsOverSelfPay = useShowInsuranceOverSelfPay();

    const [shouldNavigate, setShouldNavigate] = React.useState(false);

    return (
        <div
            className={styles["schedule-item"]}
            style={{ background: style.background }}
        >
            <Dialog open={modalInView === "cancel"}>
                <DialogContent
                    title="Cancel session"
                    variant="center"
                    onSaveClick={() => setShouldNavigate(true)}
                    handleCloseDialog={() => handleModalInView(null)}
                    isCancelBtnDisabled={isCancelSessionMutating > 0}
                    isSubmitBtnDisabled={isCancelSessionMutating > 0}
                    onCancelClick={() => setShouldNavigate(false)}
                    cancelText="Yes,cancel"
                    saveText="No,create No show note"
                    canCancelButtonSubmit
                    cancelBtnFormValue="cancel_client_session"
                    submitBtnFormValue="cancel_client_session"
                >
                    <CancelSessionForm
                        onFormSubmit={() => {
                            setSessionHistoryId(null);
                            handleModalInView(null);
                        }}
                        shouldNavigate={shouldNavigate}
                        sessionHistoryId={sessionHistoryId}
                        action="cancel"
                    />
                </DialogContent>
            </Dialog>
            <Dialog open={modalInView === "terminate"}>
                <DialogContent
                    title="Terminate Client"
                    variant="center"
                    onSaveClick={() => setShouldNavigate(true)}
                    handleCloseDialog={() => handleModalInView(null)}
                    isCancelBtnDisabled={isCancelSessionMutating > 0}
                    isSubmitBtnDisabled={isCancelSessionMutating > 0}
                    onCancelClick={() => handleModalInView(null)}
                    cancelText="Cancel"
                    saveText="Terminate & Write Note"
                    submitBtnFormValue="cancel_client_session"
                >
                    <CancelSessionForm
                        onFormSubmit={() => {
                            setSessionHistoryId(null);
                            handleModalInView(null);
                            setExternalSessionId(null);
                            setIntakeClientId(null);
                        }}
                        shouldNavigate={shouldNavigate}
                        sessionHistoryId={sessionHistoryId}
                        externalSessionId={externalSessionId}
                        intakeClientId={intakeClientId}
                        action="terminate"
                    />
                </DialogContent>
            </Dialog>
            <div
                style={{
                    backgroundColor: `${style.borderLeft}`,
                }}
                className="absolute -left-[1.5px] w-[2px] h-full top-0"
            />
            <div className="rbc-display_name">
                {`${displayNameInRightFormat({
                    firstName: schedule.first_name,
                    lastName: schedule.last_name,
                })}`}
            </div>
            <div className="rbc-requested-service">
                {makeStringFirstLetterCapital(
                    removeEnumUnderscore(schedule.session_type)
                )}
            </div>
            {/* {schedule.insurance_provider || schedule.self_pay ? (
                <div className="text-black my-4 font-medium">
                    {schedule.insurance_provider
                        ? makeStringFirstLetterCapital(
                              removeEnumUnderscore(schedule.insurance_provider)
                          )
                        : schedule.self_pay || ""}
                </div>
            ) : null} */}
            <div className="rbc-wrapper">
                <Badge
                    bgColor={
                        getColorStylesForCalendarId(
                            schedule.recurrence_rule as string
                        ).tagBgColor
                    }
                    textColor={
                        getColorStylesForCalendarId(
                            schedule.recurrence_rule as string
                        ).textColor
                    }
                    content={schedule.recurrence_rule as string}
                    className="![font-size:8px]"
                    type="custom"
                />
                {schedule.pending_task && (
                    <Badge
                        bgColor="#FFDAD9"
                        textColor="#981F41"
                        content="Pending Task"
                        className="![font-size:8px]"
                        type="custom"
                    />
                )}
                {schedule.insurance_provider ||
                (schedule.self_pay && !showInsOverSelfPay) ? (
                    <Badge
                        bgColor="#ACECF5"
                        textColor="#1A738D"
                        content={
                            schedule.insurance_provider
                                ? removeEnumUnderscore(
                                      schedule.insurance_provider
                                  )
                                : "SELF PAY"
                        }
                        className="![font-size:8px]"
                        type="custom"
                    />
                ) : null}
            </div>
            <div className="flex justify-between mt-auto">
                <div className="flex items-center" style={{ fontSize: "8px" }}>
                    {`${convertTimeTo12HoursFormat(
                        `${schedule.session_start_hr}:${schedule.session_start_min}`
                    )} - ${convertTimeTo12HoursFormat(
                        `${schedule.session_end_hr}:${schedule.session_end_min}`
                    )}`}
                </div>
                {schedule.session_history_id && (
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <MoreButton />
                        </DropdownTrigger>
                        <DropdownContent width="auto" align="end">
                            {/* <DropdownItem>Confirm follow-up</DropdownItem>
                            <DropdownItem>
                                <div className="fg two flex">
                                    Pause/unpause client <ToggleSwitch />
                                </div>
                            </DropdownItem>
                            <DropdownItem>Change appointment</DropdownItem>
                            <DropdownItem>Mark as float</DropdownItem>
                            <DropdownItem>Remove from caseload</DropdownItem> */}

                            <DropdownItem
                                className="text-xs"
                                onClick={() => {
                                    handleModalInView("cancel");
                                    setSessionHistoryId(
                                        schedule.session_history_id || ""
                                    );
                                }}
                            >
                                Cancel session
                            </DropdownItem>
                            <DropdownItem
                                className="text-xs"
                                onClick={() => {
                                    handleModalInView("terminate");
                                    setSessionHistoryId(
                                        schedule.session_history_id || ""
                                    );
                                    setExternalSessionId(
                                        schedule.external_session_id || ""
                                    );
                                    setIntakeClientId(
                                        schedule.intake_client_id
                                    );
                                }}
                            >
                                Terminate client
                            </DropdownItem>
                        </DropdownContent>
                    </Dropdown>
                )}
            </div>
        </div>
    );
}

export default function DashboardCaseloadView() {
    const [searchParams] = useSearchParams();

    const { practice } = useAppSelector((state) => state.userPractice);

    const clientFilter = searchParams.get("search_string") || "";
    const filter = searchParams.get("filter");

    const debouncedClientFilter = useDebounce(clientFilter);

    const { isLoading, error, data, isSuccess } = useFetchProvidersCaseload({
        providerId: practice?.provider_id || "",
        enabled: Boolean(practice?.provider_id),
        search_string: debouncedClientFilter,
        show_paused_client: "false",
        show_clients_with_pending_tasks:
            filter === "clients_with_pending_task" ? "true" : "",
    });

    let providerSchedule: ProviderCaseloadApp[] = [];

    data?.data?.forEach((caseload) => {
        const dataFromAppointments =
            caseload.appointments &&
            caseload.appointments.map((app) => ({
                session_type: app.session_type,
                recurrence_rule: app.recurrence_rule,
                session_day: app.session_day,
                session_start_hr: app.session_start_hr,
                session_start_min: app.session_start_min,
                session_end_hr: app.session_end_hr,
                session_end_min: app.session_end_min,
                first_name: caseload.client.first_name,
                last_name: caseload.client.last_name,
                pending_task: caseload.client.pending_task,
                is_paused: caseload.client.is_paused,
                self_pay: caseload.client.self_pay || false,
                insurance_provider:
                    caseload.client.insurance_provider_name || "",
                session_history_id: app.session_history_id,
                client_id: caseload.client.client_id,
                external_session_id: app.external_session_id,
                intake_client_id: caseload.client.intake_client_id,
            }));

        providerSchedule = providerSchedule.concat(dataFromAppointments);
    });

    const allSchedulesByDay: { [key: string]: ProviderCaseloadApp[] } = {
        sun: [],
        mon: [],
        tue: [],
        wed: [],
        thu: [],
        fri: [],
        sat: [],
    };

    // Add schedules for the rest of the month.
    providerSchedule?.forEach((s) => {
        try {
            allSchedulesByDay[s.session_day.toLowerCase()].push(s);
        } catch (e) {
            return [];
        }
        return null;
    });

    const getScheduleListGroupByWeekday = (weekDay: string) => {
        return (
            <div className={styles["schedule-grid-container"]}>
                {sortCaseloads(allSchedulesByDay[weekDay]).map((s) => {
                    return (
                        <div
                            key={`${s.session_start_hr} - ${s.session_start_min}`}
                            className="shadow-none"
                        >
                            <SingleSchedule schedule={s} />
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <>
            {isLoading && <Loader />}
            {error && (
                <ListState
                    isError
                    errorMsg={
                        error.response?.data.message ||
                        "Could not load user caseload at this time"
                    }
                    stateHelperText="Try reloading this page to solve this issue"
                    context="general"
                />
            )}
            {data?.data &&
                isSuccess &&
                providerSchedule &&
                providerSchedule.length === 0 && (
                    <ListState
                        stateHelperText="Your caseload will appear here when available"
                        emptyMessage="No caseload to display"
                        context="general"
                        isError={false}
                    />
                )}
            {data?.data &&
                isSuccess &&
                providerSchedule &&
                providerSchedule.length > 0 && (
                    <div className={styles["schedule-list-view"]}>
                        {Object.keys(allSchedulesByDay).map((day, dayIndex) => (
                            <div
                                key={`${dayIndex + 1}`}
                                className={styles["schedule-day"]}
                            >
                                <h5>{moment().day(dayIndex).format("dddd")}</h5>
                                {getScheduleListGroupByWeekday(day)}
                            </div>
                        ))}
                    </div>
                )}
        </>
    );
}
